import { useState, useCallback, useRef } from "react";

import { getChatApi } from "@/chatApi";
import { submitFormOnly, generateIdempotencyKey } from "@/services/formSubmit";
import { FormInstance, FormSubmitResult } from "@/types";
import { trackEvent } from "@/tracking";

interface UseFormOnlyParams {
  /** TOMIS site slug for API calls */
  siteId: string;
  /** The required form key that must be displayed */
  requiredFormKey: string;
  /** Initial forms from the load endpoint */
  initialForms: FormInstance[];
  /** Callback to handle errors (e.g., network errors, validation errors) */
  onError?: (error: string) => void;
  /** Callback for successful actions (e.g., form submit confirmations) */
  onSuccess?: (message: string) => void;
}

interface UseFormOnlyReturn {
  /** The active form instance (based on requiredFormKey) */
  activeForm: FormInstance | null;
  /** All available forms */
  forms: FormInstance[];
  /** Whether a form submission is in progress */
  isSubmitting: boolean;
  /** Whether the form has been successfully submitted */
  isSubmitted: boolean;
  /** Result of the last form submission */
  submitResult: FormSubmitResult | null;
  /** Update form field values */
  updateFormValues: (patch: Record<string, any>) => void;
  /** Submit the current form */
  submitForm: () => Promise<boolean>;
  /** Reset the form to initial state (for allowing resubmission) */
  resetForm: () => void;
}

/**
 * Hook for managing form-only mode state and submissions.
 *
 * This hook is specifically for form-only mode where the widget displays
 * only a form (no chat). It uses the dedicated /forms/submit endpoint.
 *
 * Key differences from useRuntime:
 * - No conversation history management
 * - No message sending capability
 * - Uses /forms/submit endpoint instead of /message
 * - Simpler state management focused solely on form handling
 */
export const useFormOnly = ({
  siteId,
  requiredFormKey,
  initialForms,
  onError,
  onSuccess,
}: UseFormOnlyParams): UseFormOnlyReturn => {
  const [forms, setForms] = useState<FormInstance[]>(initialForms);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState<FormSubmitResult | null>(
    null,
  );

  // Store idempotency key for the current submission attempt.
  // A new key is generated before each submitForm() call.
  const idempotencyKeyRef = useRef<string>(generateIdempotencyKey());

  // Get the active form based on the required form key
  const activeForm = forms.find((f) => f.key === requiredFormKey) || null;

  /**
   * Update form field values optimistically
   */
  const updateFormValues = useCallback(
    (patch: Record<string, any>) => {
      setForms((prev) =>
        prev.map((f) =>
          f.key === requiredFormKey
            ? { ...f, values: { ...(f.values ?? {}), ...patch } }
            : f,
        ),
      );
    },
    [requiredFormKey],
  );

  /**
   * Submit the current form via the dedicated /forms/submit endpoint.
   * In preview mode, skips the real API call and returns a mock success.
   */
  const submitForm = useCallback(async (): Promise<boolean> => {
    if (!activeForm) {
      onError?.("No form available to submit.");
      return false;
    }

    if (isSubmitting) {
      return false;
    }

    const isPreview = getChatApi().chat.preview;

    if (isPreview) {
      setIsSubmitting(true);
      const previewResult: FormSubmitResult = {
        type: "form_submit",
        form_key: requiredFormKey,
        status: "success",
        message: "Preview mode — no message was sent.",
        turn_id: "",
      };
      setSubmitResult(previewResult);
      setIsSubmitted(true);
      onSuccess?.(previewResult.message);
      setIsSubmitting(false);
      return true;
    }

    setIsSubmitting(true);

    // Generate a new idempotency key for each user-initiated submission.
    // This ensures that editing form values and resubmitting is treated as a
    // new request, not a retry of the previous (potentially failed) submission.
    idempotencyKeyRef.current = generateIdempotencyKey();

    try {
      const formValues = activeForm.values ?? {};

      const { data, error } = await submitFormOnly(
        siteId,
        requiredFormKey,
        formValues,
        idempotencyKeyRef.current,
      );

      if (error || !data) {
        const errorMessage = error || "Failed to submit form";
        onError?.(errorMessage);
        setSubmitResult({
          type: "form_submit",
          form_key: requiredFormKey,
          status: "error",
          message: errorMessage,
          turn_id: "",
        });
        return false;
      }

      // Update forms from response
      if (data.forms) {
        setForms(data.forms);
      }

      setSubmitResult(data.result);
      setIsSubmitted(true);

      // Handle success/error based on result status
      if (data.result.status === "success") {
        if (requiredFormKey === "handoff_sms") {
          trackEvent("lead_captured_sms");
        } else if (requiredFormKey === "handoff_live_chat") {
          trackEvent("lead_captured_webchat");
        } else if (requiredFormKey === "handoff_webhook") {
          trackEvent("lead_captured_webhook");
        }

        onSuccess?.(
          data.result.message || "Thanks! We'll reach out via SMS soon.",
        );
        return true;
      } else {
        // Server returned an error status (validation error, etc.)
        onError?.(data.result.message);
        return false;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Unknown error occurred";
      onError?.(errorMessage);
      setSubmitResult({
        type: "form_submit",
        form_key: requiredFormKey,
        status: "error",
        message: errorMessage,
        turn_id: "",
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [activeForm, isSubmitting, siteId, requiredFormKey, onError, onSuccess]);

  /**
   * Reset the form to allow resubmission
   */
  const resetForm = useCallback(() => {
    setIsSubmitted(false);
    setSubmitResult(null);
    setForms(initialForms);
  }, [initialForms]);

  return {
    activeForm,
    forms,
    isSubmitting,
    isSubmitted,
    submitResult,
    updateFormValues,
    submitForm,
    resetForm,
  };
};
